import React, { useRef, useState } from "react";
import { Form, Button, InputGroup } from "react-bootstrap";
import { Link } from "react-router-dom";
import { PAISES, PAIS_OTRO, PAIS_POR_DEFECTO } from "../data/paises";
import { REGISTRO_ABIERTO } from "../data/events";

// La validacion del telefono (libphonenumber) pesa unos 80 KB: se carga aparte,
// al tocar el campo o al enviar, para no frenar la primera carga de la landing.
let phoneLib = null;
const loadPhoneLib = () => {
    if (!phoneLib) {
        phoneLib = import("libphonenumber-js/min");
    }
    return phoneLib;
};

const EMPTY = {
    nombre: '',
    pais: PAIS_POR_DEFECTO,
    telefono: '',
    email: '',
    ciudad: '',
    // Honeypot: oculto para humanos. Si llega con algo, es un bot.
    campo_extra: ''
};

/**
 * Formulario de registro. Se monta dos veces en la landing (hero y cierre),
 * asi que cada instancia necesita su propio `instanceId`: sin el, los `id`
 * del honeypot se repetirian en el DOM y el `htmlFor` apuntaria al primero.
 *
 * `variant` solo cambia el aspecto: "hero" sobre la foto, "solid" sobre fondo claro.
 */
function FormularioRegistro({ instanceId = 'hero', variant = 'hero', title, subtitle }) {
    const [formData, setFormData] = useState(EMPTY);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Marca de tiempo para descartar envios instantaneos (antispam).
    const formLoadedAt = useRef(Date.now());

    const honeypotId = `campo_extra_${instanceId}`;

    const handleChange = (e) => {
        const { name, value } = e.target;
        // El telefono solo admite digitos. Con "Otro pais" se escribe completo,
        // con su codigo, asi que ahi tambien se deja el "+" del principio.
        let nextValue = value;
        if (name === 'telefono') {
            nextValue = formData.pais === PAIS_OTRO
                ? value.replace(/(?!^\+)[^\d]/g, '')
                : value.replace(/\D/g, '');
        }

        // Actualizacion funcional: no depende del `formData` capturado en el
        // render actual.
        setFormData((prev) => ({ ...prev, [name]: nextValue }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setIsSubmitting(true);

        // El numero sale de aqui completo, con el codigo de pais (+525512345678):
        // register.php ya no tiene que adivinar el pais. Si no es un numero
        // valido para el pais elegido, se corrige aqui y no llega a Brevo ni
        // al envio de WhatsApp, donde fallaria.
        const pais = PAISES.find((p) => p.iso === formData.pais) || PAISES[0];
        let telefono;
        try {
            const { parsePhoneNumberFromString } = await loadPhoneLib();
            const esOtro = pais.iso === PAIS_OTRO;
            const raw = esOtro && !formData.telefono.startsWith('+') ? `+${formData.telefono}` : formData.telefono;
            const parsed = parsePhoneNumberFromString(raw, esOtro ? undefined : pais.iso);
            if (!parsed || !parsed.isValid()) {
                setError(esOtro
                    ? 'Revisa tu número: escríbelo completo, con el código de tu país (por ejemplo +61 412 345 678).'
                    : pais.iso === 'MX'
                        ? 'Revisa tu número: un celular de México tiene 10 dígitos, sin el código de país.'
                        : `Revisa tu número: no parece un celular válido de ${pais.nombre}. Escríbelo sin el código de país.`);
                setIsSubmitting(false);
                return;
            }
            telefono = parsed.number;
        } catch (loadError) {
            // Sin la libreria (red caida a mitad): se manda como antes y
            // register.php valida lo basico.
            telefono = formData.telefono.startsWith('+') ? formData.telefono : `+${pais.codigo}${formData.telefono.replace(/\D/g, '')}`;
        }

        try {
            const response = await fetch('/api/register.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...formData,
                    telefono,
                    formLoadedAt: formLoadedAt.current
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || data.error || 'Error al enviar el formulario');
            }

            setSuccess(data.message);

            // /gracias lo lee para reportar la conversion con la ciudad a Meta
            // y a GA4, y para armar el archivo de calendario.
            try {
                window.sessionStorage.setItem('pc_registro_ciudad', formData.ciudad);
            } catch (storageError) {
                // sessionStorage bloqueado: el registro igual se completo.
            }

            setFormData(EMPTY);
            window.location.href = '/gracias';
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={`form form--${variant}`}>
            <Form onSubmit={handleSubmit}>
                {title && <p className="form__title">{title}</p>}
                {subtitle && <p className="form__subtitle">{subtitle}</p>}

                <Form.Group className="mb-3">
                    <Form.Label>Nombre completo</Form.Label>
                    <Form.Control
                        type="text"
                        name="nombre"
                        value={formData.nombre}
                        onChange={handleChange}
                        autoComplete="name"
                        required
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label htmlFor={`telefono_${instanceId}`}>WhatsApp</Form.Label>
                    <InputGroup>
                        <Form.Select
                            name="pais"
                            value={formData.pais}
                            onChange={(e) => {
                                handleChange(e);
                                loadPhoneLib();
                            }}
                            aria-label="País de tu número"
                            className="form__pais"
                        >
                            {PAISES.map((p) => (
                                <option key={p.iso} value={p.iso}>
                                    {/* El codigo primero: en el celular el selector es angosto y
                                        corta el final del texto. */}
                                    {p.iso === PAIS_OTRO ? p.nombre : `+${p.codigo} ${p.nombre}`}
                                </option>
                            ))}
                        </Form.Select>
                        <Form.Control
                            id={`telefono_${instanceId}`}
                            type="tel"
                            name="telefono"
                            value={formData.telefono}
                            onChange={handleChange}
                            onFocus={loadPhoneLib}
                            placeholder={formData.pais === 'MX'
                                ? 'Celular (10 dígitos)'
                                : formData.pais === PAIS_OTRO ? '+ código y número' : 'Número de celular'}
                            autoComplete={formData.pais === PAIS_OTRO ? 'tel' : 'tel-national'}
                            inputMode={formData.pais === PAIS_OTRO ? 'tel' : 'numeric'}
                            required
                        />
                    </InputGroup>
                    <span className="small">
                        {formData.pais === PAIS_OTRO
                            ? 'Escribe tu número completo, con el código de tu país.'
                            : 'Elige el país de tu número y escríbelo sin el código de país.'}
                    </span>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Correo electrónico</Form.Label>
                    <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        autoComplete="email"
                        required
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Ciudad a donde asistirás</Form.Label>
                    <Form.Select
                        name="ciudad"
                        value={formData.ciudad}
                        onChange={handleChange}
                        required
                    >
                        <option value="">Selecciona una ciudad</option>
                        <option value="cdmx">Ciudad de México (6 de octubre)</option>
                        <option value="guadalajara">Guadalajara (8 de octubre)</option>
                        <option value="monterrey">Monterrey (10 de octubre)</option>
                    </Form.Select>
                </Form.Group>

                {/* Honeypot. Fuera de pantalla en vez de `display:none`: hay
                    bots que se saltan los campos ocultos por display. El
                    nombre es neutro a proposito — con `website` o `url` los
                    gestores de contraseñas lo autocompletan y tumbarian
                    registros legitimos. */}
                <div className="hp" aria-hidden="true">
                    <label htmlFor={honeypotId}>No llenes este campo</label>
                    <input
                        type="text"
                        id={honeypotId}
                        name="campo_extra"
                        value={formData.campo_extra}
                        onChange={handleChange}
                        tabIndex={-1}
                        autoComplete="off"
                        data-lpignore="true"
                        data-1p-ignore="true"
                        data-form-type="other"
                    />
                </div>

                {error && <div className="alert alert-danger" role="alert">{error}</div>}
                {success && <div className="alert alert-success" role="alert">{success}</div>}
                {isSubmitting && (
                    <div className="alert alert-info" role="alert">
                        Estamos procesando tu registro y enviándote un correo con la información. Un momento por favor...
                    </div>
                )}

                <Button variant="primary" type="submit" className="button" disabled={isSubmitting}>
                    {isSubmitting ? 'Un momento...' : 'Quiero mi cupo gratis'}
                </Button>

                <span className="k">
                    Al enviar tu registro aceptas la{" "}
                    <Link to="/privacidad">
                        política de privacidad y de tratamiento de datos personales
                    </Link>.
                </span>
            </Form>
        </div>
    );
}

/**
 * Lo que ocupa el lugar del formulario con el registro cerrado. Va en un
 * componente aparte y no como un `return` temprano dentro del formulario: los
 * hooks de este no pueden quedar detras de una condicion.
 */
function RegistroCerrado({ variant = 'hero' }) {
    return (
        <div className={`form form--${variant} form--cerrado`}>
            <p className="form__title">Registro cerrado</p>
            <p className="form__subtitle">
                La Conferencia para Canadá ya pasó por Ciudad de México, Guadalajara y Monterrey.
                Gracias a todos los que nos acompañaron.
            </p>
            <p className="form__nota">
                Síguenos en{" "}
                <a href="https://www.instagram.com/para.canada/" target="_blank" rel="noreferrer">Instagram</a>
                {" "}y{" "}
                <a href="https://web.facebook.com/profile.php?id=100072255423424&_rdc=1&_rdr" target="_blank" rel="noreferrer">Facebook</a>
                {" "}para enterarte de las próximas fechas.
            </p>
            <p className="form__nota">
                ¿Tienes alguna pregunta? Escríbenos a{" "}
                <a href="mailto:info@para-canada.com">info@para-canada.com</a>.
            </p>
        </div>
    );
}

function RegistrationForm(props) {
    return REGISTRO_ABIERTO ? <FormularioRegistro {...props} /> : <RegistroCerrado variant={props.variant} />;
}

export default RegistrationForm;
